import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { alpha } from "@mui/material/styles";
import { useDropzone, FileRejection } from "react-dropzone";

const REQUIRED_COLUMNS = "Resident Id, SF Lease Number, TK Lease Number";

interface OptionalCsvStepProps {
  file: File | undefined;
  error: string | null;
  onSelect: (file: File) => void;
  onRemove: () => void;
}

export const OptionalCsvStep: React.FC<OptionalCsvStepProps> = ({
  file,
  error,
  onSelect,
  onRemove,
}) => {
  // Route both accepted and rejected drops through onSelect so the hook does the
  // extension/size validation in one place and surfaces a consistent message.
  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      const selected = accepted[0] ?? rejections[0]?.file;
      if (selected) onSelect(selected);
    },
    [onSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "text/csv": [".csv"] },
  });

  return (
    <Grid container spacing={2.5} direction="column">
      <Grid item>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Optionally upload a CSV with current membership data and lease numbers.
          </Typography>
          <Chip
            label="optional"
            size="small"
            sx={{
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
              fontWeight: 600,
            }}
          />
        </Box>
      </Grid>

      <Grid item>
        {file ? (
          <Chip
            label={file.name}
            onDelete={onRemove}
            sx={{
              maxWidth: "100%",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
              fontWeight: 500,
              "& .MuiChip-deleteIcon": { color: "primary.main" },
            }}
          />
        ) : (
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "primary.main" : "divider",
              borderRadius: 1,
              py: 5,
              px: 3,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: isDragActive ? "action.hover" : "transparent",
              transition: "background-color 0.15s, border-color 0.15s",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <input {...getInputProps()} />
            <AttachFileIcon sx={{ color: "text.secondary", fontSize: 32 }} />
            <Typography variant="body2" color="text.secondary">
              {isDragActive ? "Drop the CSV file here..." : "Choose CSV file or drag & drop"}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Required columns: {REQUIRED_COLUMNS}
            </Typography>
          </Box>
        )}
      </Grid>

      {error && (
        <Grid item>
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        </Grid>
      )}

      <Grid item>
        <Typography variant="body2" color="text.secondary">
          Lease numbers from the file will override existing ones before migration runs. If no file
          is uploaded, migration proceeds with current lease numbers.
        </Typography>
      </Grid>
    </Grid>
  );
};
